'use client';

// Vehículos vendidos y reservados se muestran solo por 3 días desde la fecha de venta/reserva
// Sold and reserved vehicles are shown only for 3 days from the sale/reservation date

import { memo, useCallback, useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

/** Debe coincidir con el `sm:gap-5` del contenedor de la grilla de vehículos. */
const GRID_GAP = '1.25rem';

/**
 * Separación entre la barra de categorías pegada y el panel de filtros pegado
 * debajo. Se usa para calcular el `top` del panel a partir de la altura REAL
 * de la barra (ver `offsetPanel`).
 */
const SEPARACION_PANEL = 8;
/** `top` del panel mientras la barra todavía no se midió: el `top-24` histórico. */
const OFFSET_PANEL_INICIAL = 96;

import {
  Button,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  ScrollShadow,
} from '@heroui/react';
import {
  Drawer as VaulDrawer,
  DrawerContent as VaulDrawerContent,
  DrawerHeader as VaulDrawerHeader,
  DrawerTitle as VaulDrawerTitle,
} from '@/components/ui/drawer';

import { Icon } from '@iconify/react';
import NewVehicleFilters from './new-vehicle-filters';
import VehicleFiltersBar from './vehicle-filters-bar';
import { useEnsureVehicles } from '@/hooks/useEnsureVehicles';
import { Vehicle } from '@/utils/types';
import VehicleVerticalCard from '@/components/vehicles/VehicleVerticalCard';
import VehicleHorizontalCard from '@/components/vehicles/VehicleHorizontalCard';
import VehicleCardSkeleton from '@/components/vehicles/VehicleCardSkeleton';
import useMediaQuery from '@/hooks/useMediaQuery';
import useThemeStore from '@/store/useThemeStore';
import Link from 'next/link';
import useClientStore from '@/store/useClientStore';
import { useChatbotWidgetUrl } from '@/hooks/useChatbotWidgetUrl';
import { dealershipLabel, whatsAppUrlPorPais } from '@/utils/functions';
import { isBrasil, isUsa } from '@/lib/country';
import type { CardTitleField, SpecSlot } from '@/lib/vehicleCardFields';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import useVehicleFiltersStore from '@/store/useVehicleFiltersStore';
import { filtroFacturableDisponible, isFacturable } from '@/lib/facturable';
import {
  countTractions,
  countTransmissions,
  matchesSpec,
  normalizeTraction,
  normalizeTransmission,
} from '@/lib/vehicleSpecs';
import { useVehicleFiltersUrl } from '@/hooks/useVehicleFiltersUrl';
import { OFFER_TAB_PARAM } from '@/lib/vehicleFiltersUrl';
import { compararDisponibilidad, etiquetaOrdenActivo, opcionesOrdenAgrupadas } from '@/lib/vehicleSort';
import {
  compararPorOrden,
  ordenDelTenant,
  type OrdenVehiculos,
} from '@/lib/orden-vehiculos';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { useCerrarConAtras } from '@/hooks/useCerrarConAtras';
import { useListNavigationMemory } from '@/hooks/useListNavigationMemory';
import { markLeavingList } from '@/lib/listNavigation';
import {
  aplicarAlcanceDeCategorias,
  claveDeAlcance,
  nombresParaOfrecerComoPestana,
  type ModoAlcanceCategorias,
} from '@/lib/categoryScope';

// ============================================
// BOTONES FLOTANTES DEL CELULAR ("Filtros" y "Ordenar")
// ============================================
//
// Viven en su propio componente, a nivel de módulo y con `memo`, porque su
// visibilidad depende del SCROLL. Antes el estado `showMobileButtons` y su
// listener estaban en la sección: cada `scroll` llamaba a `setState` en el
// componente que también renderiza el listado entero, y cada cambio de
// visibilidad volvía a pintar decenas de tarjetas para mostrar u ocultar dos
// botones. Acá el estado cambia y sólo se vuelve a renderizar esto.
//
// El listener sólo se registra en el celular: en escritorio los botones están
// ocultos (`md:hidden`) y escuchar el scroll era trabajo para nada. Al cruzar
// el breakpoint el efecto se rehace, así que el comportamiento se conserva
// en los dos sentidos.

interface BotonesFlotantesMovilProps {
  /** `true` por debajo de `md`. Es cuando los botones existen y se escucha el scroll. */
  esMovil: boolean;
  activeFiltersCount: number;
  filtrosLabel: string;
  ordenarLabel: string;
  onAbrirFiltros: () => void;
  onAbrirOrden: () => void;
}

const BotonesFlotantesMovil = memo(function BotonesFlotantesMovil({
  esMovil,
  activeFiltersCount,
  filtrosLabel,
  ordenarLabel,
  onAbrirFiltros,
  onAbrirOrden,
}: BotonesFlotantesMovilProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!esMovil) {
      setVisible(false);
      return;
    }

    // Se muestran cuando la sección de vehículos está a la vista y ya se
    // scrolleó un poco dentro: su tope pasó el 70% del alto de la ventana y su
    // final todavía no subió por encima del 30%.
    const handleScroll = () => {
      const section = document.getElementById('vehicles-section');
      if (!section) return;
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const isInSection = rect.top < windowHeight * 0.7 && rect.bottom > windowHeight * 0.3;
      setVisible((prev) => (prev === isInSection ? prev : isInSection));
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [esMovil]);

  return (
    <div
      className={`md:hidden fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-6 z-40 flex flex-col items-start gap-2 transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
      aria-hidden={!visible}
    >
      <button
        type='button'
        onClick={onAbrirFiltros}
        className='flex items-center gap-2 px-4 py-3 bg-white dark:bg-dark-card rounded-full border border-slate-200 dark:border-dark-border shadow-lg hover:shadow-xl transition-all duration-200'
      >
        <Icon icon='solar:filter-linear' className='text-lg text-primary' />
        <span className='text-sm font-medium text-gray-700 dark:text-white'>{filtrosLabel}</span>
        {activeFiltersCount > 0 && (
          <span className='w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-medium'>
            {activeFiltersCount}
          </span>
        )}
      </button>

      <button
        type='button'
        onClick={onAbrirOrden}
        className='flex items-center gap-2 px-4 py-3 bg-white dark:bg-dark-card rounded-full border border-slate-200 dark:border-dark-border shadow-lg hover:shadow-xl transition-all duration-200'
      >
        <Icon icon='mdi:sort' className='text-lg text-primary' />
        <span className='text-sm font-medium text-gray-700 dark:text-white'>{ordenarLabel}</span>
      </button>
    </div>
  );
});

// ============================================
// MOTOR DE BÚSQUEDA INTELIGENTE v6.0 - IA + LOCAL
// ============================================
// - Usa IA (GPT) para parsear búsquedas en lenguaje natural
// - Fallback local para búsqueda básica de texto
// - Cache inteligente para reducir llamadas API
// ============================================

import { parseSearchQuery } from '@/lib/ai-search';

// Normalizar texto (quitar acentos, minúsculas)
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Interfaz para filtros parseados por IA
interface AIFilters {
  brand?: string;
  model?: string;
  category?: string;
  yearMin?: number;
  yearMax?: number;
  priceMin?: number;
  priceMax?: number;
  mileageMin?: number;
  mileageMax?: number;
  fuelType?: string;
  transmission?: string;
  color?: string;
  features?: string[];
  searchTerms?: string[];
}

// ============================================
// BÚSQUEDA LOCAL SIMPLIFICADA (Fallback)
// ============================================

// Aplicar filtros de IA a vehículos
const applyAIFilters = (vehicles: Vehicle[], filters: AIFilters): Vehicle[] => {
  return vehicles.filter(vehicle => {
    // Filtro de marca
    if (filters.brand) {
      const vehicleBrand = normalizeText(vehicle.brand?.name || '');
      const filterBrand = normalizeText(filters.brand);
      if (!vehicleBrand.includes(filterBrand) && !filterBrand.includes(vehicleBrand)) {
        return false;
      }
    }

    // Filtro de modelo
    if (filters.model) {
      const vehicleModel = normalizeText(vehicle.model?.name || '');
      const filterModel = normalizeText(filters.model);
      if (!vehicleModel.includes(filterModel)) {
        return false;
      }
    }

    // Filtro de categoría
    if (filters.category) {
      const vehicleCategory = normalizeText(vehicle.category?.name || '');
      const filterCategory = normalizeText(filters.category);
      if (!vehicleCategory.includes(filterCategory) && !filterCategory.includes(vehicleCategory)) {
        return false;
      }
    }

    // Filtros de año
    if (filters.yearMin && (vehicle.year || 0) < filters.yearMin) return false;
    if (filters.yearMax && (vehicle.year || 0) > filters.yearMax) return false;

    // Filtros de precio
    if (filters.priceMin && (vehicle.price || 0) < filters.priceMin) return false;
    if (filters.priceMax && (vehicle.price || 0) > filters.priceMax) return false;

    // Filtros de kilometraje
    if (filters.mileageMin && (vehicle.mileage || 0) < filters.mileageMin) return false;
    if (filters.mileageMax && (vehicle.mileage || 0) > filters.mileageMax) return false;

    // Filtro de transmisión
    if (filters.transmission) {
      const vehicleTransmission = normalizeText(vehicle.transmission || '');
      const filterTransmission = normalizeText(filters.transmission);
      if (!vehicleTransmission.includes(filterTransmission)) {
        return false;
      }
    }

    // Filtro de combustible
    if (filters.fuelType) {
      const vehicleFuel = normalizeText(vehicle.fuel_type?.name || '');
      const filterFuel = normalizeText(filters.fuelType);
      if (!vehicleFuel.includes(filterFuel) && !filterFuel.includes(vehicleFuel)) {
        return false;
      }
    }

    // Filtro de color
    if (filters.color) {
      const vehicleColor = normalizeText(vehicle.color?.name || '');
      const filterColor = normalizeText(filters.color);
      if (!vehicleColor.includes(filterColor)) {
        return false;
      }
    }

    return true;
  });
};

// Búsqueda de texto simple para términos restantes
const textSearch = (vehicles: Vehicle[], searchTerms: string[]): Vehicle[] => {
  if (!searchTerms || searchTerms.length === 0) return vehicles;

  return vehicles.filter(vehicle => {
    const searchableText = normalizeText([
      vehicle.brand?.name,
      vehicle.model?.name,
      vehicle.category?.name,
      vehicle.color?.name,
      vehicle.fuel_type?.name,
      vehicle.transmission,
      vehicle.title,
      vehicle.description,
      vehicle.year?.toString(),
    ].filter(Boolean).join(' '));

    return searchTerms.every(term =>
      searchableText.includes(normalizeText(term))
    );
  });
};

// Búsqueda local básica (fallback cuando IA no disponible)
const localSearch = (vehicles: Vehicle[], query: string): Vehicle[] => {
  if (!query.trim()) return vehicles;

  const terms = normalizeText(query).split(/\s+/).filter(t => t.length >= 2);
  if (terms.length === 0) return vehicles;

  return vehicles.filter(vehicle => {
    const searchableText = normalizeText([
      vehicle.brand?.name,
      vehicle.model?.name,
      vehicle.category?.name,
      vehicle.color?.name,
      vehicle.fuel_type?.name,
      vehicle.transmission,
      vehicle.title,
      vehicle.year?.toString(),
    ].filter(Boolean).join(' '));

    // Al menos un término debe coincidir
    return terms.some(term => searchableText.includes(term));
  });
};

// Generar autocompletado básico
const generateAutocomplete = (vehicles: Vehicle[], query: string): string[] => {
  if (!query || query.length < 2) return [];

  const normalized = normalizeText(query);
  const suggestions = new Set<string>();

  vehicles.forEach(v => {
    if (v.brand?.name && normalizeText(v.brand.name).includes(normalized)) {
      suggestions.add(v.brand.name);
    }
    if (v.model?.name && normalizeText(v.model.name).includes(normalized)) {
      suggestions.add(`${v.brand?.name || ''} ${v.model.name}`.trim());
    }
  });

  return [...suggestions].slice(0, 5);
};

// ID interno de la pestaña de ofertas (campaña). No es una categoría real.
const OFFER_TAB_ID = '__offers__';

/**
 * Topes del bloque inicial de tarjetas. Son los mismos que declara
 * `initialVehicleCount` en el panel del Builder (goautos-admin,
 * `componentSettings.ts`), repetidos acá porque el panel no puede imponerlos:
 * `min`/`max` acotan las flechas del input, no lo que se teclea.
 */
const TOPE_MINIMO = 4;
const TOPE_MAXIMO = 120;
const TOPE_POR_DEFECTO = 24;

/**
 * Sinónimos en portugués de las pestañas de categoría, SÓLO para sitios de
 * Brasil: si una automotora de allá carga categorías con nombre en portugués,
 * igual caen en su pestaña. Se SUMAN a los de `categoryVariants`; en cualquier
 * otro país no se usan.
 */
const SINONIMOS_CATEGORIA_PT: Record<string, string[]> = {
  Sedan: ['Sedã', 'sedã'],
  Hatchback: ['Hatch', 'hatch'],
  Pickup: ['Picape', 'picape'],
  Camioneta: ['Caminhonete', 'caminhonete'],
  Van: ['Furgão', 'furgão'],
  Coupe: ['Cupê', 'cupê', 'Esportivo', 'esportivo', 'Conversível', 'conversível'],
  Wagon: ['Perua', 'perua'],
  Trabajo: ['Veículo de trabalho', 'Veículos de trabalho', 'Veiculo de trabalho', 'Veiculos de trabalho'],
};

const CATEGORY_ICONS: Record<string, string> = {
  all: 'mdi:car-multiple',
  SUV: 'mdi:car-suv',
  Sedan: 'mdi:car',
  Hatchback: 'mdi:car-hatchback',
  Pickup: 'mdi:truck-pickup',
  Camioneta: 'mdi:truck',
  Van: 'mdi:van-passenger',
  Coupe: 'mdi:car-sports',
  Wagon: 'mdi:car-estate',
  Trabajo: 'mdi:hard-hat',
};

interface NewVehiclesSectionProps {
  /**
   * Tarjetas que se montan de entrada; el resto llega con "Ver más".
   * Sin valor (nodos ya publicados) cae al default de 24.
   */
  initialVehicleCount?: number;
  /** Ocultar título y buscador (para landing page) */
  minimal?: boolean;
  /**
   * Destapa el buscador dentro de `minimal`, que es el listado que el builder
   * monta en el home. Ausente se comporta como siempre: en `minimal` no hay
   * buscador, y fuera de `minimal` (la página /vehicles) lo hay igual que antes.
   * Lo enciende el dealer desde el panel del builder, sección por sección.
   */
  showSearch?: boolean;
  /**
   * Con qué estado arranca el panel de filtros de escritorio: 'open' lo deja
   * desplegado al cargar, 'closed' colapsado. Ausente = 'closed', que es el
   * comportamiento de siempre y lo que ven los árboles ya publicados.
   *
   * Es SÓLO el estado inicial: el panel se sigue abriendo y cerrando a mano,
   * acá no se bloquea nada. En móvil no aplica — ahí los filtros viven en un
   * drawer que se abre con un botón, y abrirlo solo al cargar taparía el
   * listado entero.
   */
  filtersInitialState?: 'open' | 'closed';
  /** Qué va en el título: 'model' (default), 'brand', 'brand_model' o 'brand_model_version' */
  cardTitleField?: CardTitleField;
  /** Datos de la tarjeta, en orden. Ausentes = los de siempre (ver `vehicleCardFields`). */
  cardSpec1?: SpecSlot;
  cardSpec2?: SpecSlot;
  cardSpec3?: SpecSlot;
  cardSpec4?: SpecSlot;
  cardSpec5?: SpecSlot;
  /** Filter style: 'buttons' (default, icon+text) or 'images' (photo cards) */
  filterStyle?: 'buttons' | 'images';
  /**
   * Cómo se PRESENTAN los filtros: 'panel' es el `<aside>` colapsable de
   * siempre; 'bar' los dibuja como una grilla de desplegables arriba del
   * listado (ver `vehicle-filters-bar`). Los filtros y su efecto son los
   * mismos: cambia sólo la disposición.
   *
   * A propósito sin default en `craft.props` aguas arriba: ausente, el
   * catálogo sigue con el panel y ningún sitio publicado cambia.
   */
  filtersLayout?: 'panel' | 'bar';
  /**
   * Las pestañas de tipo de vehículo (la barra pegada con las categorías).
   * Apagarlas es para la automotora que arma su propia fila de tipos arriba
   * —imágenes grandes con enlace a `?tipo=`— y no quiere que el visitante
   * elija lo mismo dos veces. Ausente queda `true`: las pestañas se dibujan
   * como siempre y ningún sitio publicado cambia.
   */
  showCategoryTabs?: boolean;
  /** Builder color overrides */
  filterBarBgColor?: string;
  filterBarBorderColor?: string;
  filterTextColor?: string;
  filterActiveTextColor?: string;
  accentColor?: string;
  sectionBgColor?: string;
  /** Category image overrides (only for filterStyle='images') */
  categoryImage_all?: string;
  categoryImage_SUV?: string;
  categoryImage_Sedan?: string;
  categoryImage_Hatchback?: string;
  categoryImage_Pickup?: string;
  categoryImage_Van?: string;
  categoryImage_Coupe?: string;
  categoryImage_Wagon?: string;
  /** Badge visibility */
  showBadgeCondition?: boolean;
  showBadgePromo?: boolean;
  showBadgeNew?: boolean;
  showBadgeCustom?: boolean;
  showRibbonSold?: boolean;
  showRibbonReserved?: boolean;
  showBadgeDiscount?: boolean;
  /** Grid columns per breakpoint */
  gridColsSm?: string;
  gridColsMd?: string;
  gridColsLg?: string;
  gridColsXl?: string;
  /** Campaña de ofertas: nombre visible de la pestaña (ej. "Ofertas Cyber") */
  offerTabLabel?: string;
  /** Campaña de ofertas: palabra a buscar dentro de la etiqueta del auto (ej. "cyber").
   *  La pestaña solo aparece si hay autos cuya etiqueta la contenga. */
  offerTabFilter?: string;
  /**
   * Alcance de categorías de ESTE catálogo (ticket #86): `only` lo deja con las
   * categorías elegidas, `except` las saca. Sin configurar —lo que traen todos
   * los nodos ya publicados— muestra el inventario completo.
   */
  categoryScopeMode?: ModoAlcanceCategorias;
  categoryScopeIds?: (number | string)[];
  /**
   * Nombres de las categorías elegidas, escritos por el panel junto a los ids
   * (ticket #97). Sólo deciden qué pestañas se DIBUJAN: el filtrado sigue
   * siendo por id, y un vehículo no publicado nunca entra al listado.
   */
  categoryScopeNames?: string[];
  /**
   * Pone los vehículos disponibles antes que los reservados y los vendidos
   * (ticket #129). Dentro de cada grupo manda el orden elegido —el que el
   * visitante puso en el selector, o `date_desc` por defecto—, así que lo
   * único que cambia es el agrupado.
   *
   * Ausente o apagado se comporta EXACTAMENTE como siempre: una sola lista
   * ordenada por el criterio elegido, con reservados y vendidos intercalados.
   * Por eso también está fuera de `craft.props` en `TraditionalVehicleGrid`:
   * los nodos ya publicados no lo reciben y no hay nada que migrar.
   */
  prioritizeAvailable?: boolean;
  /** Card color overrides from builder */
  cardBgColor?: string;
  cardBorderColor?: string;
  cardTitleColor?: string;
  cardSubtitleColor?: string;
  cardSpecsColor?: string;
  cardPriceColor?: string;
}

const NewVehiclesSection = ({ minimal = false, showSearch = false, filtersInitialState, cardTitleField = 'model', cardSpec1, cardSpec2, cardSpec3, cardSpec4, cardSpec5, filterStyle = 'buttons', filtersLayout = 'panel', showCategoryTabs = true, filterBarBgColor, filterBarBorderColor, filterTextColor, filterActiveTextColor, accentColor, sectionBgColor, categoryImage_all, categoryImage_SUV, categoryImage_Sedan, categoryImage_Hatchback, categoryImage_Pickup, categoryImage_Van, categoryImage_Coupe, categoryImage_Wagon, showBadgeCondition = true, showBadgePromo = true, showBadgeNew = true, showBadgeCustom = true, showRibbonSold = true, showRibbonReserved = true, showBadgeDiscount = true, gridColsSm = '2', gridColsMd = '3', gridColsLg = '3', gridColsXl = '4', offerTabLabel, offerTabFilter, categoryScopeMode = 'all', categoryScopeIds, categoryScopeNames, cardBgColor, cardBorderColor, cardTitleColor, cardSubtitleColor, cardSpecsColor, cardPriceColor, initialVehicleCount, prioritizeAvailable = false }: NewVehiclesSectionProps) => {
  const { theme } = useThemeStore();
  const { vehicles: vehiculosDelStore, isLoading } = useEnsureVehicles();

  // Alcance de categorías del nodo (ticket #86). Se acota ACÁ, en el punto por
  // donde entra el inventario, y de ahí para abajo `vehicles` es el conjunto
  // acotado: las pestañas de categoría, los conteos del panel, el precio
  // máximo, el buscador, el contador de resultados y el 'Ver más' se derivan
  // todos de él, así que ninguno puede quedar hablando de autos que la página
  // no muestra. Sin configurar devuelve el mismo array, por referencia.
  const claveAlcance = claveDeAlcance(categoryScopeMode, categoryScopeIds);
  const vehicles = useMemo(
    () => aplicarAlcanceDeCategorias(vehiculosDelStore, categoryScopeMode, categoryScopeIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vehiculosDelStore, claveAlcance]
  );
  const { client } = useClientStore();
  // Si la automotora tiene un chatbot embebido, oculta este FAB de WhatsApp
  // (el chatbot lo reemplaza). Ver useChatbotWidgetUrl.
  const chatbotWidgetUrl = useChatbotWidgetUrl();
  const isMd = useMediaQuery('(min-width: 768px)');
  const { t } = useTranslation();
  // Textos fijos de esta sección (catalog.*), por el i18n del sitio: un sitio
  // de EE.UU. los ve en inglés (instancia `i18nEn`). Si la clave no resuelve,
  // el español de siempre.
  const tx = (key: string, fallback: string) => {
    const val = t?.(key) ?? '';
    return !val || val === key ? fallback : val;
  };
  /**
   * Orden inicial que configuró la automotora (`clients.web_vehicle_order`).
   * Sale de la fila del tenant que el store ya tiene: ninguna consulta nueva.
   */
  const ordenTenant = ordenDelTenant(
    (client as Record<string, unknown> | null)?.web_vehicle_order
  );
  // País del tenant: SOLO para lo que es propio de cada país (Facturable/IVA,
  // teléfono, sinónimos de categoría). Cualquier país que no sea BR = lo de siempre.
  const country = client?.country;
  const esBrasil = isBrasil(country);
  // EE.UU.: los pocos rótulos fijos de esta sección que no pasan por el i18n
  // (aria-labels, la pestaña de ofertas sin nombre) van en inglés. El resto de
  // los países, los de siempre.
  const enUsa = isUsa(country);
  const conFacturable = filtroFacturableDisponible(country);

  // Extract unique values for filters
  const brands = useMemo(() => {
    const uniqueBrands = new Map();
    vehicles.forEach((v) => {
      if (v.brand?.id && v.brand?.name) {
        uniqueBrands.set(v.brand.id, v.brand);
      }
    });
    return Array.from(uniqueBrands.values());
  }, [vehicles]);

  const models = useMemo(() => {
    const uniqueModels = new Map();
    vehicles.forEach((v) => {
      if (v.model?.id && v.model?.name) {
        uniqueModels.set(v.model.id, { ...v.model, brand_id: v.brand?.id });
      }
    });
    return Array.from(uniqueModels.values());
  }, [vehicles]);

  const categories = useMemo(() => {
    const uniqueCategories = new Map();
    vehicles.forEach((v) => {
      if (v.category?.id && v.category?.name) {
        uniqueCategories.set(v.category.id, v.category);
      }
    });
    return Array.from(uniqueCategories.values());
  }, [vehicles]);

  const fuelTypes = useMemo(() => {
    const uniqueFuelTypes = new Map();
    vehicles.forEach((v) => {
      if (v.fuel_type?.id && v.fuel_type?.name) {
        uniqueFuelTypes.set(v.fuel_type.id, v.fuel_type);
      }
    });
    return Array.from(uniqueFuelTypes.values());
  }, [vehicles]);

  const conditions = useMemo(() => {
    const uniqueConditions = new Map();
    vehicles.forEach((v) => {
      if (v.condition?.id && v.condition?.name) {
        uniqueConditions.set(v.condition.id, v.condition);
      }
    });
    return Array.from(uniqueConditions.values());
  }, [vehicles]);

  const colors = useMemo(() => {
    const uniqueColors = new Map();
    vehicles.forEach((v) => {
      if (v.color?.id && v.color?.name) {
        uniqueColors.set(v.color.id, v.color);
      }
    });
    return Array.from(uniqueColors.values());
  }, [vehicles]);

  const availableYears = useMemo(() => {
    return [...new Set(vehicles.map((v) => v.year).filter(Boolean))]
      .map(String)
      .sort((a, b) => b.localeCompare(a));
  }, [vehicles]);

  // Sucursales con stock (el embed dealership ya viene en useVehiclesStore)
  const dealerships = useMemo(() => {
    const uniques = new Map();
    vehicles.forEach((v) => {
      if (v.dealership?.id) uniques.set(v.dealership.id, v.dealership);
    });
    return Array.from(uniques.values())
      .map((d) => ({ id: d.id, name: dealershipLabel(d, country) }))
      .filter((d) => d.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [vehicles, country]);

  // Calcular el precio máximo real de todos los vehículos disponibles
  // Los dos ceros son la defensa: `Math.max()` sin argumentos devuelve
  // -Infinity y, con el catálogo vacío, ese valor baja al slider de precio
  // (`maxValue`), donde react-aria lo vuelve NaN y rompe el render de toda la
  // sección. Misma guarda que ya tiene `VehiclesPageClient`.
  const maxPrice = Math.max(
    0,
    ...vehicles
      .filter((v) => v.price && v.price > 0) // Only filter by having a valid price
      .map((v) => v.price || 0),
    0
  );

  // Estado de filtros global con Zustand
  const {
    filters,
    priceRange,
    sortOrder,
    searchQuery,
    setPriceRange,
    setSortOrder,
    setSearchQuery,
    clearFilters,
  } = useVehicleFiltersStore();

  // Conteos por slug normalizado, iguales a los de /vehicles: las dos
  // superficies comparten el panel, así que también tienen que compartir de
  // dónde salen las opciones.
  const transmissionCounts = useMemo(() => countTransmissions(vehicles), [vehicles]);
  const tractionCounts = useMemo(() => countTractions(vehicles), [vehicles]);
  /**
   * Facturable: la lógica ya existía y estaba probada, pero este listado no le
   * pasaba los conteos al panel, así que el acordeón NUNCA se dibujaba acá — y
   * un link con ?facturable=si se ignoraba en silencio. Ticket #21.
   */
  const clientSellsExempt = !!(client as any)?.ventas_exentas_iva;
  const facturableCounts = useMemo(() => {
    let si = 0;
    let no = 0;
    vehicles.forEach((v) => (isFacturable(v as any, clientSellsExempt) ? si++ : no++));
    return { si, no };
  }, [vehicles, clientSellsExempt]);

  /** El store guarda string o string[] según cuántos se marcaron. */
  const asArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.map(String) : v === undefined || v === null || v === '' ? [] : [String(v)];

  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Filtros en la URL, para que el link se pueda compartir (ticket #22). Este
  // listado es el del home —lo monta TraditionalVehicleGrid desde el builder—, y
  // hasta ahora sólo /vehicles escribía sus filtros. La pestaña de categoría va
  // aparte porque vive en estado local y no en el store.
  const { clearUrl } = useVehicleFiltersUrl(maxPrice, !isLoading, {
    value: selectedCategory === OFFER_TAB_ID ? OFFER_TAB_PARAM : selectedCategory,
    onChange: (value) =>
      setSelectedCategory(value === OFFER_TAB_PARAM ? OFFER_TAB_ID : value),
  });

  // Sólo el ARRANQUE del panel de escritorio: después se abre y se cierra con
  // la flecha como siempre. Sin la prop, colapsado, igual que hasta ahora.
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(filtersInitialState !== 'open');
  // Columnas efectivas por breakpoint: con el panel de filtros abierto se
  // muestra una columna menos, igual que antes.
  const colFor = (v: unknown, def: number) => {
    const n = parseInt(String(v ?? ''), 10);
    const base = Number.isFinite(n) && n > 0 ? Math.min(6, n) : def;
    return isFiltersCollapsed ? base : Math.max(1, base - 1);
  };
  const gridCols = {
    sm: colFor(gridColsSm, 2),
    md: colFor(gridColsMd, 3),
    lg: colFor(gridColsLg, 3),
    xl: colFor(gridColsXl, 4),
  };

  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState<string[]>([]);
  const [aiFilters, setAiFilters] = useState<AIFilters>({});
  const [isAISearching, setIsAISearching] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const activeView = 'grid'; // Fixed to grid view

  /**
   * Cuantas tarjetas se MONTAN de entrada. Antes se renderizaba la lista
   * completa: medido en un tenant con 428 vehiculos publicados, la home llegaba
   * a 113 tarjetas, 6.872 nodos y 165 imagenes de una sola vez, con un
   * documento de 66.613px de alto y First Contentful Paint de 4,5s.
   *
   * `initialVehicleCount` llega desde el Builder. Los nodos YA PUBLICADOS no
   * traen la prop —no esta en `craft.props`, igual que `cardSpec1..5`— asi que
   * caen a este default y no hay que migrar nada.
   *
   * 24 es multiplo de 2, 3, 4 y 6, que son todas las configuraciones de
   * columnas posibles: la ultima fila nunca queda coja.
   *
   * El valor se sanea porque el panel del Builder no puede imponer su propio
   * rango: `min`/`max` acotan las flechas del input, no lo que el usuario
   * teclea. Sin esto, un 500 escrito a mano monta 500 tarjetas —justo lo que
   * este tope existe para evitar— y vaciar el campo manda NaN, que deja
   * `slice(0, NaN)` y la grilla vacia.
   *
   * `?? NaN` y no `?? 24`: un NaN guardado se serializa como `null`, y
   * `Number(null)` es 0 —no NaN—, asi que sin esto volveria como el minimo en
   * vez del default.
   */
  const crudo = Number(initialVehicleCount ?? NaN);
  const porPagina = Number.isFinite(crudo)
    ? Math.min(TOPE_MAXIMO, Math.max(TOPE_MINIMO, Math.floor(crudo)))
    : TOPE_POR_DEFECTO;
  /** Contenedor de la grilla: de ahí sale la tarjeta que ancla la posición. */
  const gridRef = useRef<HTMLDivElement | null>(null);

  /**
   * Firma por VALOR de la lista que se está viendo. Reemplaza las dependencias
   * por identidad del efecto de reseteo de más abajo: `filters` y `priceRange`
   * salen del store como objetos nuevos, así que ese efecto se disparaba
   * también al montar y tiraba abajo los "Ver más" recuperados.
   */
  const listSignature = useMemo(
    () =>
      JSON.stringify([
        searchQuery,
        selectedCategory,
        filters,
        priceRange,
        sortOrder,
        aiFilters,
        offerTabFilter ?? '',
        porPagina,
      ]),
    [searchQuery, selectedCategory, filters, priceRange, sortOrder, aiFilters, offerTabFilter, porPagina]
  );

  // Memoria de navegación: cuántas tarjetas había montadas y a qué altura
  // estaba el usuario cuando tocó una, para que volver de la ficha no lo
  // devuelva al principio del home.
  const { initialCursor, remember: rememberListPosition } =
    useListNavigationMemory({
      scope: 'home',
      signature: listSignature,
      ready: !isLoading,
      gridRef,
    });

  // Arranca SIEMPRE en el tope inicial, igual que el servidor. Este listado
  // —a diferencia del de /vehicles— se renderiza en el server, así que leer la
  // posición guardada acá hacía que el cliente montara más tarjetas que el HTML
  // recibido y React rompía la hidratación (error #418, medido en el home de
  // Mallorca y Demo Argentina). La recuperación va en el efecto de abajo, que
  // corre recién en el cliente.
  const [visibles, setVisibles] = useState(porPagina);

  useLayoutEffect(() => {
    if (initialCursor && initialCursor > porPagina) setVisibles(initialCursor);
    // Sólo al montar: después manda el usuario con "Ver más".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La memoria necesita saber cuántas tarjetas hay montadas para poder anotar
  // la foto mientras el usuario scrollea, no sólo cuando toca una tarjeta.
  useEffect(() => {
    rememberListPosition(visibles);
  }, [visibles, isLoading, rememberListPosition]);

  /**
   * Se dispara en captura, antes de que el Link de la tarjeta navegue: guarda
   * la foto y deja la marca con la que la ficha sabe que su "Volver" tiene que
   * ser un Atrás de verdad.
   */
  const onGridClickCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.('a[href^="/vehicles/"]') as
        | HTMLAnchorElement
        | null;
      if (!link) return;
      rememberListPosition(visibles);
      markLeavingList(new URL(link.href, window.location.origin).pathname);
    },
    [rememberListPosition, visibles]
  );

  // Llamar a la IA para parsear la búsqueda (con debounce)
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setAiFilters({});
      return;
    }

    const debounceTimer = setTimeout(async () => {
      setIsAISearching(true);
      try {
        const result = await parseSearchQuery(searchQuery, {
          brands: brands.map(b => ({ id: b.id, name: b.name })),
          categories: categories.map(c => ({ id: c.id, name: c.name })),
        });
        setAiFilters(result.filters || {});
      } catch (error) {
        console.warn('AI search error, using local fallback:', error);
        setAiFilters({});
      } finally {
        setIsAISearching(false);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, brands, categories]);

  // Generar autocompletado cuando cambia el query
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const suggestions = generateAutocomplete(vehicles, searchQuery);
      setAutocompleteResults(suggestions);
      setShowAutocomplete(suggestions.length > 0);
    } else {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    }
  }, [searchQuery, vehicles]);

    // Localized categories and sort options
  // Mapeo de IDs de filtro a posibles nombres en la DB (con variantes de acentos/idioma)
  const categoryVariants: Record<string, string[]> = {
    SUV: ['SUV', 'Suv', 'suv', 'Crossover', 'crossover', 'Todoterreno', '4x4'],
    Sedan: ['Sedan', 'Sedán', 'sedan', 'sedán', 'Berlina', 'Automóvil', 'automóvil', 'Clásico', 'clásico'],
    Hatchback: ['Hatchback', 'hatchback', 'Compacto', 'City Car', 'city car'],
    // `Camioneta` sale de acá a propósito: es una categoría propia del catálogo
    // y tiene su propia pestaña. Agrupadas, no había forma de ver por separado
    // los Pickup de las Camionetas ni de filtrar una sola.
    Pickup: ['Pickup', 'pickup', 'Pick-up'],
    Camioneta: ['Camioneta', 'camioneta'],
    Van: ['Van', 'van', 'Minivan', 'minivan', 'Furgoneta', 'Furgón', 'furgón'],
    Coupe: ['Coupe', 'Coupé', 'coupe', 'coupé', 'Deportivo', 'deportivo', 'Convertible', 'convertible'],
    Wagon: ['Wagon', 'wagon', 'Station Wagon', 'station wagon', 'Familiar'],
    // Ticket #97: camionetas de faena (minería, forestal, obra). Categoría
    // propia, no una variante de Pickup: el punto es poder verlas aparte.
    Trabajo: ['Vehículo de trabajo', 'Vehiculo de trabajo', 'Vehículos de trabajo', 'Vehiculos de trabajo'],
  };
  if (esBrasil) {
    for (const [catId, extra] of Object.entries(SINONIMOS_CATEGORIA_PT)) {
      categoryVariants[catId] = [...(categoryVariants[catId] || []), ...extra];
    }
  }

  // Obtener categorías que tienen vehículos
  //
  // Ticket #97: además del inventario, las categorías que el dealer eligió a
  // mano en modo `only`. Sin esto el sitio publicado no dibuja la pestaña que
  // el Builder sí muestra, y lo configurado y lo publicado dejan de coincidir.
  //
  // Es SÓLO la lista de pestañas. El listado de abajo se sigue armando con
  // `vehicles`, que ya viene filtrado por visibilidad: una categoría elegida sin
  // stock publicado dibuja su pestaña y cae en el estado vacío de siempre. No
  // hay forma de que esto muestre un auto que no está publicado.
  const categoriesWithVehicles = useMemo(() => {
    const categorySet = new Set<string>();
    vehicles.forEach(v => {
      if (v.category?.name) {
        categorySet.add(v.category.name);
      }
    });
    nombresParaOfrecerComoPestana(
      categoryScopeMode,
      categoryScopeIds,
      categoryScopeNames
    ).forEach(n => categorySet.add(n));
    return categorySet;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, categoryScopeMode, claveAlcance, (categoryScopeNames || []).join('|')]);

  // Verificar si un filtro tiene vehículos (comparando con variantes)
  const categoryHasVehicles = (catId: string): boolean => {
    if (catId === 'all') return true;
    const variants = categoryVariants[catId] || [catId];
    return variants.some(variant => categoriesWithVehicles.has(variant));
  };

  const allCategories = [
    { id: 'all', name: t('vehicles.categories.all'), icon: CATEGORY_ICONS.all, description: t('vehicles.categoryDescriptions.all') },
    { id: 'SUV', name: t('vehicles.categories.suv'), icon: CATEGORY_ICONS.SUV, description: t('vehicles.categoryDescriptions.suv') },
    { id: 'Sedan', name: t('vehicles.categories.sedan'), icon: CATEGORY_ICONS.Sedan, description: t('vehicles.categoryDescriptions.sedan') },
    { id: 'Hatchback', name: t('vehicles.categories.hatchback'), icon: CATEGORY_ICONS.Hatchback, description: t('vehicles.categoryDescriptions.hatchback') },
    { id: 'Pickup', name: t('vehicles.categories.pickup'), icon: CATEGORY_ICONS.Pickup, description: t('vehicles.categoryDescriptions.pickup') },
    { id: 'Camioneta', name: t('vehicles.categories.camioneta'), icon: CATEGORY_ICONS.Camioneta, description: t('vehicles.categoryDescriptions.camioneta') },
    { id: 'Van', name: t('vehicles.categories.van'), icon: CATEGORY_ICONS.Van, description: t('vehicles.categoryDescriptions.van') },
    { id: 'Coupe', name: t('vehicles.categories.coupe'), icon: CATEGORY_ICONS.Coupe, description: t('vehicles.categoryDescriptions.coupe') },
    { id: 'Wagon', name: t('vehicles.categories.wagon'), icon: CATEGORY_ICONS.Wagon, description: t('vehicles.categoryDescriptions.wagon') },
    { id: 'Trabajo', name: t('vehicles.categories.trabajo'), icon: CATEGORY_ICONS.Trabajo, description: t('vehicles.categoryDescriptions.trabajo') },
  ];

  // Filtrar solo categorías que tienen vehículos (siempre mostrar "all")
  const baseCategories = allCategories.filter(cat => categoryHasVehicles(cat.id));

  // ── Pestaña de OFERTAS (campaña) ──
  // Solo aparece si hay autos cuya etiqueta contiene la palabra configurada.
  // Cuando la campaña termina (se borran las etiquetas), la pestaña desaparece sola.
  const offerKeyword = (offerTabFilter || '').trim().toLowerCase();
  const hasOfferVehicles = useMemo(() => {
    if (!offerKeyword) return false;
    return vehicles.some(v => (v.label || '').toLowerCase().includes(offerKeyword));
  }, [vehicles, offerKeyword]);

  const vehicleCategories = useMemo(() => {
    if (!hasOfferVehicles) return baseCategories;
    const offerTab = {
      id: OFFER_TAB_ID,
      name: (offerTabLabel || '').trim() || (enUsa ? 'Deals' : 'Ofertas'), // igual en portugués
      icon: 'mdi:fire',
      description: '',
    };
    return [offerTab, ...baseCategories];
  }, [hasOfferVehicles, baseCategories, offerTabLabel, enUsa]);

  // Resolver nombre de categoría DB → ID de filtro
  const resolveCategoryId = (dbName: string): string | null => {
    for (const [catId, variants] of Object.entries(categoryVariants)) {
      if (variants.some(v => v.toLowerCase() === dbName.toLowerCase())) return catId;
    }
    return null;
  };

  // Build category → image map for image-style filters
  const categoryImages = useMemo(() => {
    if (filterStyle !== 'images') return {};
    const map: Record<string, string | null> = { all: vehicles.find(v => v.main_image)?.main_image ?? null };
    for (const v of vehicles) {
      const catName = v.category?.name;
      if (catName && v.main_image) {
        // Mapear tanto el nombre exacto de la DB como el ID del filtro
        if (!map[catName]) map[catName] = v.main_image;
        const catId = resolveCategoryId(catName);
        if (catId && !map[catId]) map[catId] = v.main_image;
      }
    }
    // Apply custom overrides
    if (categoryImage_all) map.all = categoryImage_all;
    if (categoryImage_SUV) map.SUV = categoryImage_SUV;
    if (categoryImage_Sedan) map.Sedan = categoryImage_Sedan;
    if (categoryImage_Hatchback) map.Hatchback = categoryImage_Hatchback;
    if (categoryImage_Pickup) map.Pickup = categoryImage_Pickup;
    if (categoryImage_Van) map.Van = categoryImage_Van;
    if (categoryImage_Coupe) map.Coupe = categoryImage_Coupe;
    if (categoryImage_Wagon) map.Wagon = categoryImage_Wagon;
    return map;
  }, [filterStyle, vehicles, categoryImage_all, categoryImage_SUV, categoryImage_Sedan, categoryImage_Hatchback, categoryImage_Pickup, categoryImage_Van, categoryImage_Coupe, categoryImage_Wagon]);

  // Las mismas cuatro opciones en el panel expandido, el riel colapsado y el
  // drawer del celular (y en /vehicles). El orden por defecto (`date_desc`)
  // no está en la lista: ver `@/lib/vehicleSort`.
  // Igual que en /vehicles: el botón cerrado muestra criterio + dirección.
  const etiquetaActiva = etiquetaOrdenActivo(sortOrder);
  const sortOrderLabel = etiquetaActiva ? (t(etiquetaActiva.i18n) || etiquetaActiva.fallback) : undefined;

  const sortOptions = opcionesOrdenAgrupadas(sortOrder).map((o) => ({
    ...o,
    label: t(o.i18n) || o.fallback,
  }));

  const clearAllFilters = () => {
    clearFilters(maxPrice);
    setSelectedCategory('all');
    // Vacía los params de filtro y deja los que no lo son (promo, section).
    clearUrl();
  };

  // Filtrado de vehículos usando IA + búsqueda local
  const filteredVehicles = useMemo(() => {
    let result = vehicles;

    // PASO 1: Aplicar filtros de IA (si hay)
    if (Object.keys(aiFilters).length > 0) {
      result = applyAIFilters(result, aiFilters);

      // También aplicar búsqueda de texto para términos adicionales
      if (aiFilters.searchTerms && aiFilters.searchTerms.length > 0) {
        result = textSearch(result, aiFilters.searchTerms);
      }
    } else if (searchQuery.trim()) {
      // Fallback: búsqueda local simple mientras IA carga o si falla
      result = localSearch(result, searchQuery);
    }

    // Helper para verificar si un valor está en un filtro (soporta arrays y strings)
    const matchesFilter = (filterValue: string | string[] | undefined, vehicleValue: string | undefined): boolean => {
      if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true;
      if (!vehicleValue) return false;
      if (Array.isArray(filterValue)) {
        return filterValue.includes(vehicleValue);
      }
      return filterValue === vehicleValue;
    };

    // PASO 2: Aplicar filtros adicionales
    result = result.filter((vehicle) => {
      let matches = true;

      // Tab de OFERTAS: filtra por etiqueta del auto en vez de categoría.
      if (selectedCategory === OFFER_TAB_ID) {
        if (offerKeyword && !(vehicle.label || '').toLowerCase().includes(offerKeyword)) {
          matches = false;
        }
      } else if (selectedCategory !== 'all') {
        // Category from tabs (usar variantes para matchear nombres con acentos/sinónimos)
        const variants = categoryVariants[selectedCategory] || [selectedCategory];
        const vehicleCatName = vehicle?.category?.name || '';
        if (!variants.some(v => v.toLowerCase() === vehicleCatName.toLowerCase())) {
          matches = false;
        }
      }

      // Filters from sidebar (ahora soportan multi-select)
      if (!matchesFilter(filters.brand, vehicle?.brand?.id?.toString())) {
        matches = false;
      }

      if (!matchesFilter(filters.model, vehicle?.model?.id?.toString())) {
        matches = false;
      }

      if (!matchesFilter(filters.category, vehicle?.category?.id?.toString())) {
        matches = false;
      }

      if (!matchesFilter(filters.fuel_type, vehicle?.fuel_type?.id?.toString())) {
        matches = false;
      }

      if (!matchesFilter(filters.condition, vehicle?.condition?.id?.toString())) {
        matches = false;
      }

      if (!matchesFilter(filters.color, vehicle?.color?.id?.toString())) {
        matches = false;
      }

      if (!matchesFilter(filters.dealership, (vehicle?.dealership?.id ?? vehicle?.dealership_id)?.toString())) {
        matches = false;
      }

      // Mismos tres filtros que /vehicles, con la MISMA normalización: un link
      // compartido tiene que dar el mismo resultado en las dos pantallas.
      if (!matchesSpec(asArray(filters.transmission), normalizeTransmission(vehicle?.transmission))) {
        matches = false;
      }

      if (!matchesSpec(asArray(filters.traction), normalizeTraction((vehicle as any)?.traction))) {
        matches = false;
      }

      // En Brasil no se aplica (el filtro no existe allá) aunque el link traiga ?facturable=.
      const fact = conFacturable ? asArray(filters.facturable) : [];
      if (fact.length > 0) {
        const quiere = fact.includes('si');
        const esFacturable = isFacturable(vehicle as any, clientSellsExempt);
        if (fact.length === 1 && esFacturable !== quiere) matches = false;
      }

      if (!matchesFilter(filters.year, vehicle?.year?.toString())) {
        matches = false;
      }

      if (vehicle?.price < priceRange[0] || vehicle?.price > priceRange[1]) {
        matches = false;
      }

      return matches;
    });

    // PASO 3: Ordenar (solo si NO hay búsqueda activa, ya que smartSearch ya ordena por relevancia)
    if (searchQuery.trim() === '') {
      // El criterio vive en `lib/orden-vehiculos`, igual que en /vehicles y en
      // las grillas del Builder (ticket #164). Acá había un `switch` copiado
      // que NO miraba `web_priority`: la base mandaba el listado con los "Muy
      // alta" adelante y este comparador lo reordenaba al hidratar, así que la
      // prioridad no se veía en el home.
      const porOrden = compararPorOrden(sortOrder as OrdenVehiculos, ordenTenant);
      result = [...result].sort((a, b) => {
        // Ticket #129: con la opción encendida, los disponibles van antes que
        // los reservados y los vendidos. Es criterio EXTERNO —si los dos caen
        // en el mismo grupo devuelve 0— así que el orden de abajo sigue
        // decidiendo dentro de cada grupo, tal cual estaba elegido.
        if (prioritizeAvailable) {
          const porEstado = compararDisponibilidad(a, b);
          if (porEstado !== 0) return porEstado;
        }
        return porOrden(a, b);
      });
    } else if (prioritizeAvailable) {
      // Con búsqueda activa el orden lo pone la relevancia de `smartSearch`, y
      // el switch de arriba no corre. La prioridad se aplica igual, por fuera:
      // agrupa por disponibilidad y, como `sort` es estable, dentro de cada
      // grupo se conserva la relevancia que venía. Apagado no se toca nada.
      result = [...result].sort(compararDisponibilidad);
    }

    return result;
  }, [vehicles, searchQuery, selectedCategory, filters, priceRange, sortOrder, ordenTenant, aiFilters, offerKeyword, conFacturable, esBrasil, prioritizeAvailable]);

  /**
   * Al cambiar cualquier criterio se vuelve al tope inicial: si alguien pidio
   * "ver mas" tres veces sobre SUV y despues cambia a Pickup, no tiene sentido
   * arrancar mostrando 96 pickups.
   */
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (lastSignature.current === null) {
      lastSignature.current = listSignature;
      return;
    }
    if (lastSignature.current === listSignature) return;
    lastSignature.current = listSignature;
    setVisibles(porPagina);
  }, [listSignature, isLoading, porPagina]);

  /** Lo que realmente se monta. El contador de resultados sigue usando el total. */
  const vehiculosVisibles = useMemo(
    () => filteredVehicles.slice(0, visibles),
    [filteredVehicles, visibles]
  );
  const quedanPorMostrar = Math.max(0, filteredVehicles.length - vehiculosVisibles.length);

  // Generar sugerencias cuando no hay resultados
  const noResultsSuggestions = useMemo(() => {
    if (filteredVehicles.length === 0 && searchQuery.trim()) {
      return generateAutocomplete(vehicles, searchQuery);
    }
    return [];
  }, [filteredVehicles.length, searchQuery, vehicles]);

  // El orden no suma: no acota la lista, sólo la acomoda. Contarlo hacía que el
  // botón flotante del celular mostrara "1" y apareciera "Limpiar" con la lista
  // completa a la vista. Mismo criterio que el panel y que /vehicles.
  const activeFiltersCount =
    // Sin filtro Facturable (Brasil), un ?facturable= de un link no suma.
    Object.keys(filters).filter((k) => conFacturable || k !== 'facturable').length +
    (priceRange[0] > 0 || priceRange[1] < maxPrice ? 1 : 0) +
    (selectedCategory !== 'all' ? 1 : 0) +
    (searchQuery.trim() !== '' ? 1 : 0);

  // Si el catálogo se acota (ticket #86) y la pestaña elegida deja de existir,
  // vuelve a "Todos": si no, queda activa una pestaña que ya no puede mostrar
  // nada y el listado se ve vacío sin explicación.
  useEffect(() => {
    if (selectedCategory === 'all' || selectedCategory === OFFER_TAB_ID) return;
    if (!vehicleCategories.some((c) => c.id === selectedCategory)) {
      setSelectedCategory('all');
    }
  }, [vehicleCategories, selectedCategory]);

  const selectedCategoryData = vehicleCategories.find(
    (cat) => cat.id === selectedCategory
  );

  const whatsappUrl = whatsAppUrlPorPais(client?.contact, undefined, country);

  // ── Tema de los drawers ──
  //
  // Los drawers se montan en `body` (portal de Vaul), o sea FUERA de la
  // sección. En el sitio del builder el tema oscuro no siempre viene de
  // `html.dark`: TraditionalVehicleGrid envuelve esta sección en un
  // `<div class="dark">` cuando el fondo elegido por el dealer es oscuro
  // (Tradecars es el caso: html claro, sección oscura). Como las variantes
  // `dark:` de Tailwind miran al ancestro, el panel de escritorio salía oscuro
  // y el drawer del celular, afuera de ese div, salía BLANCO sobre un sitio
  // negro. Al abrir se mira si la sección tiene un ancestro `.dark` y se le
  // pone la misma clase al drawer, más su fondo explícito (la clase `dark`
  // sobre un elemento no activa sus propias variantes, sólo las de adentro).
  const seccionRef = useRef<HTMLDivElement>(null);
  const [drawerOscuro, setDrawerOscuro] = useState(false);
  const detectarOscuro = () =>
    setDrawerOscuro(!!seccionRef.current?.closest('.dark'));
  const CLASES_DRAWER_OSCURO = 'dark bg-dark-bg border-dark-border text-white';

  // Callbacks estables para `BotonesFlotantesMovil`: con `memo`, una función
  // nueva por render lo haría volver a pintar igual.
  const abrirFiltros = useCallback(() => {
    detectarOscuro();
    setIsFilterDrawerOpen(true);
  }, []);
  const abrirOrden = useCallback(() => {
    detectarOscuro();
    setIsSortDrawerOpen(true);
  }, []);
  const cerrarFiltros = useCallback(() => setIsFilterDrawerOpen(false), []);
  const cerrarOrden = useCallback(() => setIsSortDrawerOpen(false), []);

  // "Atrás" del navegador cierra el drawer en vez de irse del sitio, y cerrar
  // por X / Aplicar / fondo no deja entradas de más en el historial.
  useCerrarConAtras(isFilterDrawerOpen, cerrarFiltros);
  useCerrarConAtras(isSortDrawerOpen, cerrarOrden);

  // ── Sticky del panel de filtros, calculado desde la barra de categorías ──
  //
  // La barra de categorías es `sticky top-16` y el panel de filtros era
  // `sticky top-24` (96px): dos offsets pegados independientes. Medido en un
  // cliente real, la barra ocupaba de 64 a 137px, así que el panel se pegaba
  // 41px por debajo de su tope y la barra le tapaba el encabezado, que es la
  // única vía a "Limpiar filtros".
  //
  // No se reemplaza por otro número fijo: el alto de la barra cambia con el
  // título (fuera de `minimal`), con el estilo de filtro (`images` es más
  // alto), con la cantidad de categorías con stock y con el ancho. Se mide la
  // barra —su `top` pegado más su alto real— y se deja `SEPARACION_PANEL` de
  // aire. El `ResizeObserver` vuelve a medir cuando la barra cambia de alto.
  const barraCategoriasRef = useRef<HTMLDivElement>(null);
  const [offsetPanel, setOffsetPanel] = useState(OFFSET_PANEL_INICIAL);

  useEffect(() => {
    const barra = barraCategoriasRef.current;
    if (!barra || typeof window === 'undefined') return;

    const medir = () => {
      // `top` computado de un `sticky` es su offset pegado ("64px"), esté o
      // no pegada en este momento; el rect da el alto real con borde incluido.
      const topPegado = parseFloat(window.getComputedStyle(barra).top) || 0;
      const alto = barra.getBoundingClientRect().height;
      const next = Math.round(topPegado + alto + SEPARACION_PANEL);
      setOffsetPanel((prev) => (prev === next ? prev : next));
    };

    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    ro?.observe(barra);
    window.addEventListener('resize', medir);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);

  /**
   * Alto máximo del bloque pegado (panel expandido): lo que queda del viewport
   * por debajo del offset, menos un respiro abajo. El panel scrollea adentro
   * de ese alto (columna flex + `min-h-0`), con el encabezado siempre visible.
   */
  const estiloBloquePegado = {
    top: offsetPanel,
    maxHeight: `calc(100dvh - ${offsetPanel}px - 1rem)`,
  } as React.CSSProperties;

  useEffect(() => {
    if (maxPrice > 0 && priceRange[1] === 0) {
      setPriceRange([0, maxPrice]);
    }
  }, [maxPrice]);

  return (
    <div
      id='vehicles-section'
      ref={seccionRef}
      className={`vehicle-grid-wrapper ${sectionBgColor ? '' : 'bg-slate-50/50 dark:bg-dark-bg'}`}
      style={sectionBgColor ? { backgroundColor: sectionBgColor } : undefined}
    >
      {/* Fila flex en vez de grilla: con 1 o 2 autos publicados la grilla los
          dejaba pegados a la izquierda con el resto de las columnas vacías.
          El ancho se calcula acá (y no con clases de Tailwind) porque las
          columnas por breakpoint las elige el usuario y además bajan en uno
          cuando el panel de filtros está abierto. Los números se interpolan en
          JS a propósito: dividir por una var() de CSS tiene peor soporte en
          navegadores viejos. */}
      <style>{`
        .vehicle-grid-wrapper .vehicle-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        }
        .vehicle-grid-wrapper .vehicle-grid > * { width: 100%; min-width: 0; }
        @media (min-width: 640px) { .vehicle-grid-wrapper .vehicle-grid--cols > * { width: calc((100% - ${gridCols.sm - 1} * ${GRID_GAP}) / ${gridCols.sm}); } }
        @media (min-width: 768px) { .vehicle-grid-wrapper .vehicle-grid--cols > * { width: calc((100% - ${gridCols.md - 1} * ${GRID_GAP}) / ${gridCols.md}); } }
        @media (min-width: 1024px) { .vehicle-grid-wrapper .vehicle-grid--cols > * { width: calc((100% - ${gridCols.lg - 1} * ${GRID_GAP}) / ${gridCols.lg}); } }
        @media (min-width: 1280px) { .vehicle-grid-wrapper .vehicle-grid--cols > * { width: calc((100% - ${gridCols.xl - 1} * ${GRID_GAP}) / ${gridCols.xl}); } }
      `}</style>
      {/* Botón de WhatsApp - Visible si el cliente tiene número configurado y no
          usa un chatbot embebido (ese lo reemplaza). Derecha, todas las resoluciones.

          Se esconde con un drawer abierto: es `z-[60]` y los drawers son `z-50`,
          asi que se PINTA encima del panel de filtros, justo al lado de "Aplicar
          filtros". El click igual se lo lleva el drawer —el hit-test lo
          confirma— pero verlo ahi flotando sobre el panel es confuso, y asi lo
          reporto el cliente desde un iPhone. Se oculta el boton en vez de tocar
          el z-index de drawer.tsx/sheet.tsx, que son compartidos por todos los
          overlays del sitio. */}
      {whatsappUrl && !chatbotWidgetUrl && (
        <a
          href={whatsappUrl}
          target='_blank'
          rel='noopener noreferrer'
          aria-hidden={isFilterDrawerOpen || isSortDrawerOpen}
          className={`fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-6 z-[60] p-3 bg-[#25D366] rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 ${
            isFilterDrawerOpen || isSortDrawerOpen ? 'pointer-events-none opacity-0' : 'opacity-100'
          }`}
        >
          <Icon icon='logos:whatsapp-icon' className='text-xl' />
        </a>
      )}

      {/* Botones flotantes móvil - Solo visibles en sección vehículos, izquierda.
          Componente aparte con su propio listener de scroll: ver arriba. */}
      <BotonesFlotantesMovil
        esMovil={!isMd && filtersLayout !== 'bar'}
        activeFiltersCount={activeFiltersCount}
        filtrosLabel={t('vehicles.filters.title') || 'Filtros'}
        ordenarLabel={t('pages.vehicles.orderBy') || 'Ordenar'}
        onAbrirFiltros={abrirFiltros}
        onAbrirOrden={abrirOrden}
      />

      {/* Fixed Categories Navigation */}
      {/* `var(--navbar-height)` no existe: nadie la declara en el repo (es
          interna de HeroUI y no está expuesta en `:root`), así que `top`
          resolvía a `auto` y esta barra no se pegaba nunca. `top-16` = 64px,
          que es el `h-16` que usan los tres navbars del builder.
          El `ref` es para medirla: el panel de filtros se pega justo debajo
          de esta barra, sea cual sea su alto (ver `offsetPanel`). */}
      {showCategoryTabs !== false && (
      <div
        ref={barraCategoriasRef}
        className={`sticky top-16 z-30 border-b ${!filterBarBgColor ? 'bg-white dark:bg-dark-bg border-gray-200 dark:border-dark-border' : ''}`}
        style={filterBarBgColor ? {
          backgroundColor: filterBarBgColor,
          borderColor: filterBarBorderColor || undefined,
        } : undefined}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2'>
          <div className='flex flex-col gap-2'>
            {/* Title and Actions - Oculto en modo minimal */}
            {!minimal && (
              <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h1 className='text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2'>
                      {selectedCategoryData?.name || t('vehicles.categories.all')}

                      <Chip size='sm' variant='flat' color='primary'>
                        {filteredVehicles.length}
                      </Chip>
                    </h1>

                    <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                      {selectedCategoryData?.description || t('home.featuredVehicles.subtitle')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Categories */}

            {/* `overflow-y-hidden` y `touch-action: pan-x`: ScrollShadow deja
                `overflow` en LOS DOS ejes, asi que esta fila se quedaba tambien
                con el gesto vertical y arrastrar hacia abajo sobre las
                categorias no movia la pagina. Con `pan-x` el navegador sabe que
                aca solo manda el eje horizontal y el vertical pasa de largo, y
                el scroll horizontal queda a cargo del compositor.
                `overscroll-x-contain` evita que al llegar al final se dispare
                el gesto de "atras" del navegador.
                El `px-4` dentro del riel da aire al primer y ultimo chip, que
                antes quedaban pegados al borde y se veian cortados. */}
            <ScrollShadow
              orientation='horizontal'
              className='w-full overflow-y-hidden overscroll-x-contain [touch-action:pan-x]'
            >
              <div className='flex justify-start lg:justify-center items-center w-full'>
                <div className={`flex ${filterStyle === 'images' ? 'gap-2.5' : 'gap-2'} pt-2 pb-2 px-4 lg:px-0 min-w-max mx-auto`}>
                  {vehicleCategories.map((category) => {
                    const isActive = selectedCategory === category.id;
                    const isOfferTab = category.id === OFFER_TAB_ID;
                    const hasBuilderColors = filterTextColor || filterActiveTextColor || accentColor;

                    {/* Image style filter */}
                    if (filterStyle === 'images') {
                      const img = categoryImages[category.id];
                      return (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`relative flex flex-col items-center gap-1 transition-all duration-200 hover:-translate-y-0.5 group ${
                            isActive ? 'scale-[1.02]' : ''
                          }`}
                        >
                          <div
                            className={`relative w-20 h-14 sm:w-24 sm:h-16 rounded-xl overflow-hidden transition-all duration-200 ${
                              isActive
                                ? 'ring-2 shadow-md'
                                : 'ring-1 group-hover:ring-2 group-hover:shadow-sm'
                            }`}
                            style={{
                              '--tw-ring-color': isActive ? (accentColor || 'var(--color-primary)') : (filterBarBorderColor || 'var(--color-default-300)'),
                            } as React.CSSProperties}
                          >
                            {img ? (
                              <div
                                className='absolute inset-0 bg-center bg-cover transition-transform duration-300 group-hover:scale-105'
                                style={{ backgroundImage: `url(${img})` }}
                              />
                            ) : (
                              <div className='absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-700'>
                                <Icon icon={category.icon} className='text-2xl text-gray-500 dark:text-gray-400' />
                              </div>
                            )}
                            <div className={`absolute inset-0 transition-opacity duration-200 ${
                              isActive ? 'bg-black/30' : 'bg-black/10 group-hover:bg-black/20'
                            }`} />
                            {isActive && (
                              <div className='absolute bottom-0 left-0 right-0 h-[3px]' style={{ backgroundColor: accentColor || 'var(--color-primary)' }} />
                            )}
                          </div>
                          <span
                            className={`text-[11px] sm:text-xs font-medium transition-colors duration-200 ${
                              isActive || isOfferTab ? 'font-semibold' : ''
                            }`}
                            style={{
                              color: isOfferTab
                                ? '#dc2626'
                                : isActive
                                  ? (accentColor || undefined)
                                  : (filterTextColor || undefined),
                            }}
                          >
                            {category.name}
                          </span>
                        </button>
                      );
                    }

                    {/* Button style filter (default) */}
                    return (
                      <Button
                        key={category.id}
                        variant={isOfferTab || isActive ? 'solid' : 'light'}
                        color={isOfferTab || hasBuilderColors ? undefined : (isActive ? 'primary' : 'default')}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`whitespace-nowrap hover:-translate-y-0.5 transition-all px-4 py-2 rounded-full ${
                          isActive ? 'shadow-md' : ''
                        } ${isActive && theme === 'dark' && !hasBuilderColors && !isOfferTab ? 'text-black' : ''}`}
                        style={isOfferTab
                          ? { color: '#ffffff', backgroundColor: isActive ? '#b91c1c' : '#dc2626', fontWeight: 600 }
                          : (hasBuilderColors ? {
                              color: isActive ? (filterActiveTextColor || '#ffffff') : (filterTextColor || undefined),
                              backgroundColor: isActive ? (accentColor || undefined) : undefined,
                            } : undefined)}
                        startContent={
                          <Icon icon={category.icon} className='text-xl' />
                        }
                        size='md'
                      >
                        {category.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </ScrollShadow>
          </div>
        </div>
      </div>
      )}

      {/* Search Bar - En modo minimal solo si el dealer lo encendió (`showSearch`) */}
      {/* Sin la barra de categorías el buscador queda primero en el flujo y su
          margen superior se escapa del contenedor (margin collapse), dejando ver
          el fondo de la página. En ese caso el aire va como padding. */}
      {(!minimal || showSearch) && (
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${showCategoryTabs === false ? 'pt-1' : 'mt-1'} sm:mb-2 flex flex-col sm:flex-row gap-2 sm:gap-4`}>
          {/* Buscador con Autocompletado */}
          <div className='w-full sm:flex-[3] relative'>
            <Search
              className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10'
              size={16}
            />
            <Input
              type='text'
              placeholder={t('pages.vehicles.searchPlaceholder')}
              className='
                pl-12 pr-10 py-2 min-h-[36px]
                rounded-xl
                border border-slate-300
                bg-slate-100 dark:bg-dark-card dark:border-dark-border
                text-sm text-gray-700 dark:text-gray-200
                shadow-md
                focus:border-primary focus:ring-2 focus:ring-primary
                transition-all
                w-full
                max-w-xxl
              '
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchQuery.length >= 2 && setShowAutocomplete(autocompleteResults.length > 0)}
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
            />
            {/* Botón para limpiar búsqueda */}
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors'
              >
                <Icon icon='mdi:close-circle' className='text-lg' />
              </button>
            )}

            {/* Dropdown de Autocompletado */}
            {showAutocomplete && autocompleteResults.length > 0 && (
              <div className='absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl shadow-lg z-50 overflow-hidden'>
                <div className='py-1'>
                  {autocompleteResults.map((suggestion, index) => (
                    <button
                      key={index}
                      className='w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-primary/10 dark:hover:bg-primary/20 flex items-center gap-3 transition-colors'
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchQuery(suggestion);
                        setShowAutocomplete(false);
                      }}
                    >
                      <Icon icon='mdi:magnify' className='text-gray-400 text-base' />
                      <span>{suggestion}</span>
                    </button>
                  ))}
                </div>
                {/* Tip de búsqueda */}
                <div className='px-4 py-2 bg-slate-50 dark:bg-dark-bg border-t border-slate-200 dark:border-dark-border'>
                  <p className='text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5'>
                    <Icon icon='mdi:lightbulb-outline' className='text-yellow-500' />
                    {tx('catalog.search.tip', 'Prueba: "bajo 15 millones", "no diesel", "con sunroof"')}
                  </p>
                </div>
              </div>
            )}
          </div>
          {/* Selector de orden. Solo fuera de `minimal`: ese modo ya ordena desde
              el botón flotante en móvil y desde la barra lateral en escritorio,
              así que acá sería un tercer control para lo mismo. */}
          {!minimal && (
          <div className='w-full sm:flex-1 min-w-[80px] flex mt-2 sm:mt-0 -mb-4'>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant='light'
                  startContent={<Icon icon='mdi:sort' className='text-base' />}
                  className='w-full flex items-center gap-x-1 px-3 py-2 min-h-[36px] text-sm shadow-none bg-transparent border-none hover:bg-slate-100 focus:bg-slate-100 transition-colors'
                >
                  {sortOrderLabel ||
                    t('pages.vehicles.orderBy')}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                selectionMode='single'
                selectedKeys={new Set([sortOrder])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0];
                  if (selected) setSortOrder(selected.toString());
                }}
              >
                {sortOptions.map((option) => (
                  <DropdownItem
                    key={option.key}
                    startContent={<Icon icon={option.icon} className='text-xl' />}
                  >
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
          )}
        </div>
      )}

      {/* Main Content */}

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20'>
        {/* Filtros en barra: la misma selección que el panel, en grilla y
            arriba del listado. Ver `filtersLayout` en las props. */}
        {filtersLayout === 'bar' && (
          <div className='mb-6'>
            <VehicleFiltersBar
              brands={brands}
              models={models}
              categories={categories}
              fuelTypes={fuelTypes}
              transmissionCounts={transmissionCounts}
              availableYears={availableYears}
              maxPrice={maxPrice}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              onClearFilters={clearAllFilters}
              bgColor={filterBarBgColor}
              textColor={filterTextColor}
              borderColor={filterBarBorderColor}
              accentColor={accentColor}
            />
          </div>
        )}
        <div className='flex flex-col md:flex-row gap-8'>
          {/* Filtro en desktop - sticky y colapsable.

              El bloque pegado es una COLUMNA FLEX con `max-height`: el botón
              de orden arriba (`shrink-0`) y el panel debajo, que se encoge
              (`min-h-0`) y scrollea ADENTRO, con su encabezado siempre a la
              vista y sus bordes y sombra enteros. Antes el `overflow-y-auto`
              estaba en un div de afuera: scrolleaba la tarjeta completa, el
              encabezado con "Limpiar" se iba con el scroll y el recorte se
              comía la sombra.

              "Ordenar" va acá y no sólo en el riel colapsado: en `minimal`
              (el home) no hay barra de búsqueda con selector de orden, así
              que con el panel abierto no había forma de ordenar en escritorio. */}
          {isMd && !isFiltersCollapsed && filtersLayout !== 'bar' && (
            <aside className='w-72 shrink-0'>
              <div className='sticky flex flex-col gap-2' style={estiloBloquePegado}>
                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      variant='bordered'
                      startContent={<Icon icon='mdi:sort' className='text-lg text-primary' />}
                      className='w-full shrink-0 justify-start h-11 rounded-xl bg-white dark:bg-dark-card border-slate-200 dark:border-dark-border text-sm font-medium text-gray-700 dark:text-white'
                      aria-label={t('pages.vehicles.orderBy') || 'Ordenar'}
                    >
                      <span className='truncate'>
                        {sortOrderLabel ||
                          t('pages.vehicles.orderBy') || 'Ordenar'}
                      </span>
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    selectionMode='single'
                    selectedKeys={new Set([sortOrder])}
                    onSelectionChange={(keys) => {
                      const k = Array.from(keys)[0];
                      if (k) setSortOrder(String(k));
                    }}
                  >
                    {sortOptions.map((option) => (
                      <DropdownItem key={option.key} startContent={<Icon icon={option.icon} className='text-sm' />}>
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>

                <NewVehicleFilters
                  brands={brands}
                  models={models}
                  categories={categories}
                  fuelTypes={fuelTypes}
                  conditions={conditions}
                  colors={colors}
                  dealerships={dealerships}
                  facturableCounts={facturableCounts}
                  transmissionCounts={transmissionCounts}
                  tractionCounts={tractionCounts}
                  initialOpenAccordion={filters.color ? 'color' : undefined}
                  availableYears={availableYears}
                  maxPrice={maxPrice}
                  isCollapsed={isFiltersCollapsed}
                  onToggleCollapse={() => setIsFiltersCollapsed(true)}
                  onClearFilters={clearAllFilters}
                />
              </div>
            </aside>
          )}

          {/* Botones para expandir filtros y ordenar cuando están colapsados.
              Mismo `top` que el panel expandido: se pegan justo debajo de la
              barra de categorías. */}
          {isMd && isFiltersCollapsed && filtersLayout !== 'bar' && (
            <div
              className='sticky h-fit shrink-0 flex flex-col items-center gap-2'
              style={{ top: offsetPanel }}
            >
              <button
                onClick={() => setIsFiltersCollapsed(false)}
                className='p-3 bg-white dark:bg-dark-card rounded-xl border border-slate-200 dark:border-dark-border shadow-sm hover:shadow-md hover:border-primary/50 transition-all duration-200 flex flex-col items-center gap-2'
                aria-label={enUsa ? 'Show filters' : 'Mostrar filtros'}
              >
                <Icon icon='solar:filter-linear' className='text-xl text-primary' />
                {activeFiltersCount > 0 && (
                  <span className='w-5 h-5 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-medium'>
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              <Dropdown placement='right'>
                <DropdownTrigger>
                  <button
                    className='p-3 bg-white dark:bg-dark-card rounded-xl border border-slate-200 dark:border-dark-border shadow-sm hover:shadow-md hover:border-primary/50 transition-all duration-200 flex flex-col items-center gap-2'
                    aria-label={enUsa ? 'Sort' : 'Ordenar'}
                  >
                    <Icon icon='mdi:sort' className='text-xl text-primary' />
                  </button>
                </DropdownTrigger>
                <DropdownMenu
                  selectionMode='single'
                  selectedKeys={new Set([sortOrder])}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0];
                    if (k) setSortOrder(String(k));
                  }}
                >
                  {sortOptions.map((option) => (
                    <DropdownItem key={option.key} startContent={<Icon icon={option.icon} className='text-sm' />}>
                      {option.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          )}

          {/* Vehicles Content */}

          <div className='flex-1 min-w-0'>
            {/* Vehicle Cards */}

            <div
              className={`vehicle-grid gap-4 sm:gap-5 transition-all duration-500 ${
                activeView === 'grid' ? 'vehicle-grid--cols' : ''
              } mx-auto`}
              ref={gridRef}
              onClickCapture={onGridClickCapture}
            >
              {isLoading
                ? Array(6)
                    .fill(null)

                    .map((_, index) => <VehicleCardSkeleton key={index} />)
                : vehiculosVisibles.map((vehicle) =>
                    activeView === 'grid' ? (
                      <VehicleVerticalCard
                        key={vehicle.id}
                        vehicle={vehicle}
                        cardTitleField={cardTitleField}
                        cardSpec1={cardSpec1}
                        cardSpec2={cardSpec2}
                        cardSpec3={cardSpec3}
                        cardSpec4={cardSpec4}
                        cardSpec5={cardSpec5}
                        showBadgeCondition={showBadgeCondition}
                        showBadgePromo={showBadgePromo}
                        showBadgeNew={showBadgeNew}
                        showBadgeCustom={showBadgeCustom}
                        showRibbonSold={showRibbonSold}
                        showRibbonReserved={showRibbonReserved}
                        showBadgeDiscount={showBadgeDiscount}
                        cardBgColor={cardBgColor}
                        cardBorderColor={cardBorderColor}
                        cardTitleColor={cardTitleColor}
                        cardSubtitleColor={cardSubtitleColor}
                        cardSpecsColor={cardSpecsColor}
                        cardPriceColor={cardPriceColor}
                      />
                    ) : (
                      <VehicleHorizontalCard
                        key={vehicle.id}
                        vehicle={vehicle}
                      />
                    )
                  )}
            </div>

            {/* Ver mas: agrega OTRO bloque de tarjetas al DOM, no revela
                tarjetas escondidas. Sin esto no habria ganancia de render. */}
            {!isLoading && quedanPorMostrar > 0 && (
              <div className='flex justify-center pt-8'>
                <Button
                  size='lg'
                  variant='bordered'
                  className='min-h-12 px-8 font-medium'
                  onPress={() => setVisibles((n) => n + porPagina)}
                  style={accentColor ? { borderColor: accentColor, color: accentColor } : undefined}
                >
                  {t('vehicles.page.showMore') || 'Ver más'}
                  <span className='opacity-60'>({quedanPorMostrar})</span>
                </Button>
              </div>
            )}

            {/* No Results State - Mejorado con sugerencias inteligentes */}

            {!isLoading && filteredVehicles.length === 0 && (
              <div className='text-center py-12 max-w-md mx-auto'>
                <div className='w-20 h-20 bg-slate-100 dark:bg-dark-card rounded-full flex items-center justify-center mx-auto mb-6'>
                  <Icon
                    icon='mdi:car-search'
                    className='text-4xl text-gray-400 dark:text-gray-500'
                  />
                </div>

                <h3 className='text-xl font-semibold text-gray-900 dark:text-white mb-2'>
                  {t('vehicles.page.noResults')}
                </h3>

                <p className='text-gray-500 dark:text-gray-400 mb-6'>
                  {searchQuery.trim()
                    ? tx('catalog.search.noResultsFor', 'No encontramos vehículos para "{{query}}"').replace('{{query}}', () => searchQuery)
                    : t('vehicles.page.noResultsDescription')
                  }
                </p>

                {/* Sugerencias inteligentes */}
                {noResultsSuggestions.length > 0 && (
                  <div className='mb-6'>
                    <p className='text-sm text-gray-500 dark:text-gray-400 mb-3'>
                      {tx('catalog.search.didYouMean', '¿Quisiste decir?')}
                    </p>
                    <div className='flex flex-wrap justify-center gap-2'>
                      {noResultsSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => setSearchQuery(suggestion)}
                          className='px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-full text-sm font-medium transition-colors'
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tips de búsqueda */}
                <div className='bg-slate-50 dark:bg-dark-card/50 rounded-xl p-4 mb-6 text-left'>
                  <p className='text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2'>
                    <Icon icon='mdi:lightbulb-on' className='text-yellow-500' />
                    {tx('catalog.search.tipsTitle', 'Tips de búsqueda inteligente')}
                  </p>
                  <ul className='text-xs text-gray-500 dark:text-gray-400 space-y-2'>
                    <li className='flex items-start gap-2'>
                      <Icon icon='mdi:currency-usd' className='text-green-500 mt-0.5 shrink-0' />
                      <span><strong>{tx('catalog.search.tipPrice', '"bajo 15 millones"')}</strong>{tx('catalog.search.tipPriceDesc', ' - Filtra por precio máximo')}</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <Icon icon='mdi:calendar' className='text-blue-500 mt-0.5 shrink-0' />
                      <span><strong>{tx('catalog.search.tipYear', '"2020 o más nuevo"')}</strong>{tx('catalog.search.tipYearDesc', ' - Filtra por año mínimo')}</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <Icon icon='mdi:close-circle' className='text-red-500 mt-0.5 shrink-0' />
                      <span><strong>{tx('catalog.search.tipFuel', '"no diesel"')}</strong>{tx('catalog.search.tipFuelDesc', ' - Excluye un tipo de combustible')}</span>
                    </li>
                    <li className='flex items-start gap-2'>
                      <Icon icon='mdi:star' className='text-purple-500 mt-0.5 shrink-0' />
                      <span><strong>{tx('catalog.search.tipFeature', '"con sunroof"')}</strong>{tx('catalog.search.tipFeatureDesc', ' - Busca por características')}</span>
                    </li>
                  </ul>
                </div>

                <div className='flex flex-col sm:flex-row gap-3 justify-center'>
                  <Button
                    color='primary'
                    variant='solid'
                    onPress={clearAllFilters}
                    startContent={<Icon icon='mdi:refresh' />}
                  >
                    {tx('catalog.listing.viewAll', 'Ver todos los vehículos')}
                  </Button>
                  <Button
                    color='default'
                    variant='light'
                    onPress={() => setSearchQuery('')}
                    startContent={<Icon icon='mdi:eraser' />}
                  >
                    {tx('catalog.listing.clearSearch', 'Limpiar búsqueda')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer — Vaul (arrastrable) */}
      <VaulDrawer open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
        {/* `dvh` y no `vh`: en iOS/Android `vh` cuenta la barra del navegador,
            así que el 10% de margen se lo comía la UI del browser y el footer
            con "Aplicar filtros" quedaba abajo del borde visible. */}
        {/* `dark:bg-dark-bg` en vez del `dark:bg-[#0B0B0F]` genérico del
            componente compartido, y el texto explícito: así el drawer usa el
            mismo fondo oscuro que el resto del sitio (`html.dark`) y hereda el
            tema que el cliente configuró en el builder, en vez de un negro
            propio que en Tradecars se veía como un panel ajeno. */}
        <VaulDrawerContent
          className={`h-[88dvh] max-h-[88dvh] bg-white dark:bg-dark-bg border-slate-200 dark:border-dark-border text-gray-900 dark:text-white ${
            drawerOscuro ? CLASES_DRAWER_OSCURO : ''
          }`}
        >
          {/* UN solo encabezado: título, contador, "Limpiar" y la X. Antes el
              drawer ponía su título y el panel, debajo, el suyo con el contador
              y "Limpiar": "Filtros" dos veces, una arriba de la otra. Ahora el
              panel se monta con `sinEncabezado` y todo vive acá. */}
          <VaulDrawerHeader className='shrink-0 flex flex-row items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 dark:border-dark-border text-left'>
            <div className='flex items-center gap-2 min-w-0'>
              <Icon icon='solar:filter-linear' className='text-xl text-primary shrink-0' />
              <VaulDrawerTitle className='text-base font-semibold text-gray-900 dark:text-white truncate'>
                {t('vehicles.filters.title') || 'Filtros'}
              </VaulDrawerTitle>
              {activeFiltersCount > 0 && (
                <Chip size='sm' color='primary' variant='flat' className='font-semibold shrink-0'>
                  {activeFiltersCount}
                </Chip>
              )}
            </div>
            <div className='flex items-center gap-1 shrink-0'>
              {activeFiltersCount > 0 && (
                <Button
                  size='sm'
                  variant='light'
                  color='danger'
                  onPress={clearAllFilters}
                  className='text-[13px] h-8 px-2'
                  startContent={<Icon icon='mdi:filter-remove-outline' className='text-base' />}
                >
                  {t('vehicles.filters.clearFilters') || 'Limpiar filtros'}
                </Button>
              )}
              {/* 40px: mínimo táctil. Cierra por la misma vía que "Aplicar":
                  el estado se apaga y `useCerrarConAtras` consume la entrada
                  del historial, así que no queda un paso de más. */}
              <button
                type='button'
                onClick={cerrarFiltros}
                aria-label={t('vehicles.filters.close') || 'Cerrar filtros'}
                className='w-10 h-10 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors'
              >
                <Icon icon='mdi:close' className='text-2xl' />
              </button>
            </div>
          </VaulDrawerHeader>
          {/* Mismas props que el panel de escritorio. Faltaban `models`,
              `categories`, `fuelTypes`, `conditions` y `colors`: en mobile esos
              cinco acordeones abrían vacíos, así que Modelo, Categoría,
              Combustible, Condición y Color eran inusables desde el celular.
              El scroll lo maneja el panel (columna flex), no este div.

              `data-vaul-no-drag`: sin esto Vaul ARBITRA por JavaScript cada
              gesto sobre el panel para decidir si es "arrastrar para cerrar" o
              "scrollear la lista" (pone `touch-action: none` en el drawer y
              resuelve en su `shouldDrag`). Ese arbitraje saca el scroll del
              compositor, y en un iPhone real se siente lento y con tirones —
              que es lo que reporto el cliente. Con el atributo, `shouldDrag`
              devuelve false en toda esta zona y el scroll vuelve a ser nativo.
              El drawer se sigue cerrando desde el handle de arriba, tocando el
              fondo oscuro o con "Aplicar filtros"; lo unico que se pierde es
              arrastrarlo desde el medio de la lista, que es justo el gesto que
              competia con el scroll. */}
          <div data-vaul-no-drag className='min-h-0 flex-1 flex flex-col px-4 pb-4 pt-3'>
            <NewVehicleFilters
              brands={brands}
              models={models}
              categories={categories}
              fuelTypes={fuelTypes}
              conditions={conditions}
              colors={colors}
              dealerships={dealerships}
              facturableCounts={facturableCounts}
              transmissionCounts={transmissionCounts}
              tractionCounts={tractionCounts}
              initialOpenAccordion={filters.color ? 'color' : undefined}
              availableYears={availableYears}
              maxPrice={maxPrice}
              onClearFilters={clearAllFilters}
              sinEncabezado
            />
          </div>
          <div className='shrink-0 border-t border-gray-200 dark:border-dark-border px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            <Button color='primary' onPress={cerrarFiltros} className='w-full'>
              {t('vehicles.filters.applyFilters')}
            </Button>
          </div>
        </VaulDrawerContent>
      </VaulDrawer>

      {/* Mobile Sort Drawer — Vaul (arrastrable) */}
      <VaulDrawer open={isSortDrawerOpen} onOpenChange={setIsSortDrawerOpen}>
        <VaulDrawerContent
          className={`bg-white dark:bg-dark-bg border-slate-200 dark:border-dark-border text-gray-900 dark:text-white ${
            drawerOscuro ? CLASES_DRAWER_OSCURO : ''
          }`}
        >
          <VaulDrawerHeader>
            <VaulDrawerTitle className='text-gray-900 dark:text-white'>{t('pages.vehicles.orderBy') || 'Ordenar'}</VaulDrawerTitle>
          </VaulDrawerHeader>
          <div className='flex flex-col pb-6'>
            {sortOptions.map((option) => (
              <button
                key={option.key}
                onClick={() => {
                  setSortOrder(option.key);
                  cerrarOrden();
                }}
                className={`flex items-center gap-3 px-5 py-3.5 transition-colors ${
                  sortOrder === option.key
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 dark:text-gray-300 active:bg-gray-100 dark:active:bg-dark-card'
                }`}
              >
                <Icon icon={option.icon} className='text-lg' />
                <span className='text-sm'>{option.label}</span>
                {sortOrder === option.key && (
                  <Icon icon='mdi:check' className='ml-auto text-primary text-lg' />
                )}
              </button>
            ))}
          </div>
        </VaulDrawerContent>
      </VaulDrawer>
    </div>
  );
};

export default NewVehiclesSection;
